import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { ApiError } from "@/api/client";
import { formatFileSize } from "@/lib/format";
import { EmojiPicker } from "./EmojiPicker";
import type { Conversation, SendInput } from "./queries";
import {
  SENDABLE_ACCEPT,
  fileKind,
  useSendMessage,
  useTemplatePreview,
  useTemplates,
} from "./queries";
import { URGENCY_TONES, useWindowState } from "./window";

/** A round, borderless icon button, as WhatsApp's beside its text box. */
const ICON_BUTTON =
  "flex size-[42px] shrink-0 items-center justify-center rounded-full text-wa-icon hover:bg-black/5";

/**
 * The file about to go, above the text box — which then holds its caption.
 *
 * A photo shows itself; anything else shows its name and weight, so the agent
 * sees they picked the right quote before the client does.
 */
function PickedFile({ file, onRemove }: { file: File; onRemove: () => void }) {
  const { t } = useTranslation();
  const [thumbnail, setThumbnail] = useState<string | null>(null);

  useEffect(() => {
    // `createObjectURL` is missing outside a real browser; the name is enough.
    if (fileKind(file.name) !== "image" || typeof URL.createObjectURL !== "function") {
      return;
    }
    const url = URL.createObjectURL(file);
    setThumbnail(url);
    return () => {
      URL.revokeObjectURL(url);
      setThumbnail(null);
    };
  }, [file]);

  return (
    <div className="mb-2 flex items-center gap-3 rounded-[8px] bg-white p-2">
      {thumbnail ? (
        <img src={thumbnail} alt="" className="size-12 rounded-[4px] object-cover" />
      ) : (
        <span className="flex size-12 shrink-0 items-center justify-center rounded-[4px] bg-wa-panel text-wa-icon">
          <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true">
            <path
              fill="currentColor"
              d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8zm-1 7V3.5L18.5 9z"
            />
          </svg>
        </span>
      )}
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm">{file.name}</span>
        <span className="text-xs text-wa-meta">{formatFileSize(file.size)}</span>
      </span>
      <button
        type="button"
        onClick={onRemove}
        aria-label={t("inbox.removeFile")}
        title={t("inbox.removeFile")}
        className={ICON_BUTTON}
      >
        <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
          <path
            fill="currentColor"
            d="M19 6.4 17.6 5 12 10.6 6.4 5 5 6.4l5.6 5.6L5 17.6 6.4 19l5.6-5.6 5.6 5.6 1.4-1.4-5.6-5.6z"
          />
        </svg>
      </button>
    </div>
  );
}

/**
 * Write a reply, or — once the window has closed — pick a template.
 *
 * The swap is the whole point. Outside the 24-hour window a free-form message
 * is refused by our own serializer before it ever reaches Meta, so offering a
 * text box there would be offering an action that cannot succeed. The composer
 * showing what is *possible* is what teaches the rule (.docs/03 §7).
 */
export function Composer({ conversation }: { conversation: Conversation }) {
  const { t } = useTranslation();
  const [body, setBody] = useState("");
  const [template, setTemplate] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [showEmojis, setShowEmojis] = useState(false);
  const box = useRef<HTMLTextAreaElement>(null);
  const picker = useRef<HTMLInputElement>(null);
  const send = useSendMessage(conversation.id);
  const templates = useTemplates();
  const preview = useTemplatePreview(
    conversation.id,
    template ? Number(template) : null,
  );
  const serviceWindow = useWindowState(conversation.window_expires_at);

  const submit = (input: SendInput) => {
    send.mutate(input, {
      onSuccess: () => {
        setBody("");
        setTemplate("");
        setFile(null);
      },
    });
  };

  const reply = () => {
    const text = body.trim();
    if (file) submit({ file, body: text });
    else if (text) submit({ body: text });
  };

  /** At the caret, not at the end: an emoji usually goes mid-sentence. */
  const insertEmoji = (emoji: string) => {
    const field = box.current;
    const start = field?.selectionStart ?? body.length;
    const end = field?.selectionEnd ?? body.length;
    setBody(body.slice(0, start) + emoji + body.slice(end));
    const caret = start + emoji.length;
    requestAnimationFrame(() => {
      field?.focus();
      field?.setSelectionRange(caret, caret);
    });
  };

  const closeEmojis = useCallback(() => setShowEmojis(false), []);

  const error =
    send.error instanceof ApiError
      ? send.error.message
      : send.error
        ? t("inbox.sendFailed")
        : null;

  return (
    <div className="relative bg-wa-panel px-3 py-2.5">
      {serviceWindow.isOpen && showEmojis ? (
        <EmojiPicker onPick={insertEmoji} onClose={closeEmojis} />
      ) : null}

      {/* Open, the countdown is in the thread's header, where WhatsApp says
          "en línea". Closed, this is the line that explains the templates. */}
      {serviceWindow.isOpen ? null : (
        <p className={`mb-2 text-xs ${URGENCY_TONES[serviceWindow.urgency]}`}>
          {t("inbox.windowClosed")}
        </p>
      )}

      {error ? (
        <p role="alert" className="mb-2 text-xs text-wa-danger">
          {error}
        </p>
      ) : null}

      {serviceWindow.isOpen && file ? (
        <PickedFile file={file} onRemove={() => setFile(null)} />
      ) : null}

      {serviceWindow.isOpen ? (
        <form
          className="flex items-end gap-1"
          onSubmit={(event) => {
            event.preventDefault();
            reply();
          }}
        >
          <button
            type="button"
            onClick={() => setShowEmojis((open) => !open)}
            aria-label={t("inbox.emoji")}
            aria-expanded={showEmojis}
            title={t("inbox.emoji")}
            className={`${ICON_BUTTON} ${showEmojis ? "text-wa-green-dark" : ""}`}
          >
            <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true">
              <circle
                cx="12"
                cy="12"
                r="9"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
              />
              <circle cx="9" cy="10" r="1.2" fill="currentColor" />
              <circle cx="15" cy="10" r="1.2" fill="currentColor" />
              <path
                d="M8 14c1 1.6 2.4 2.4 4 2.4s3-.8 4-2.4"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              />
            </svg>
          </button>
          <button
            type="button"
            onClick={() => picker.current?.click()}
            aria-label={t("inbox.attach")}
            title={t("inbox.attach")}
            className={ICON_BUTTON}
          >
            <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true">
              <path
                d="M16.5 6.5 8.4 14.6a1.9 1.9 0 0 0 2.7 2.7l8.2-8.2a3.8 3.8 0 0 0-5.4-5.4l-8.4 8.4a5.7 5.7 0 0 0 8.1 8.1l7.4-7.4"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              />
            </svg>
          </button>
          {/* Only what Meta takes, so a file it would refuse cannot be picked
              in the first place. The server checks again, and decides. */}
          <input
            ref={picker}
            type="file"
            accept={SENDABLE_ACCEPT}
            aria-label={t("inbox.attach")}
            className="hidden"
            onChange={(event) => {
              setFile(event.target.files?.[0] ?? null);
              // Cleared, so picking the same file again after removing it
              // still counts as a change.
              event.target.value = "";
            }}
          />
          <textarea
            ref={box}
            value={body}
            onChange={(event) => setBody(event.target.value)}
            onKeyDown={(event) => {
              // Enter sends, Shift+Enter breaks the line — the habit every
              // messaging app has already trained.
              if (event.key === "Enter" && !event.shiftKey) {
                event.preventDefault();
                reply();
              }
            }}
            rows={1}
            aria-label={t("inbox.composerLabel")}
            placeholder={
              file ? t("inbox.captionPlaceholder") : t("inbox.composerPlaceholder")
            }
            // Grows with what is typed, up to a few lines, as WhatsApp's does.
            className="max-h-36 min-h-[42px] flex-1 resize-none rounded-[8px] bg-white px-3 py-[10px] text-[15px] leading-[22px] outline-none [field-sizing:content] placeholder:text-wa-meta"
          />
          <button
            type="submit"
            disabled={(!body.trim() && !file) || send.isPending}
            aria-label={t("inbox.send")}
            title={t("inbox.send")}
            className="flex size-[42px] shrink-0 items-center justify-center rounded-full bg-wa-green text-white hover:bg-wa-green-dark disabled:opacity-50"
          >
            <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
              <path
                fill="currentColor"
                d="M3.4 20.4 21 12 3.4 3.6 3.4 10l12.6 2-12.6 2z"
              />
            </svg>
          </button>
        </form>
      ) : (
        <form
          className="flex flex-wrap items-end gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            if (template) submit({ template: Number(template) });
          }}
        >
          <label className="min-w-48 flex-1">
            <span className="text-xs text-wa-meta">{t("inbox.templateLabel")}</span>
            <select
              value={template}
              onChange={(event) => setTemplate(event.target.value)}
              className="mt-1 block h-[42px] w-full rounded-[8px] bg-white px-3 text-[15px]"
            >
              <option value="">{t("inbox.templatePick")}</option>
              {(templates.data?.results ?? []).map((option) => (
                <option key={option.id} value={option.id}>
                  {option.name}
                </option>
              ))}
            </select>
          </label>
          <button
            type="submit"
            disabled={!template || send.isPending || Boolean(preview.data?.problem)}
            className="h-[42px] rounded-full bg-wa-green px-5 text-[15px] font-medium text-white hover:bg-wa-green-dark disabled:opacity-50"
          >
            {t("inbox.sendTemplate")}
          </button>
          {/* The words, not the name: the agent approves what the client will
              read, blanks filled in, before it goes. */}
          {template && preview.data ? (
            <div className="w-full">
              <p className="text-xs text-wa-meta">{t("inbox.templatePreview")}</p>
              <p className="mt-1 ml-auto w-fit max-w-[85%] rounded-[7.5px] bg-wa-out px-3 py-2 text-sm whitespace-pre-wrap shadow-[0_1px_0.5px_rgb(11_20_26/0.13)]">
                {preview.data.text}
              </p>
              {preview.data.problem ? (
                <p className="mt-1 text-xs text-wa-danger">{preview.data.problem}</p>
              ) : null}
            </div>
          ) : null}
          {templates.data?.results?.length === 0 ? (
            <p className="w-full text-xs text-wa-danger">{t("inbox.noTemplates")}</p>
          ) : (
            <p className="w-full text-xs text-wa-meta">{t("inbox.templateHint")}</p>
          )}
        </form>
      )}
    </div>
  );
}
